import {Injectable} from '@angular/core'
import {BehaviorSubject, catchError, map, Observable, of} from "rxjs"
import {UserService} from "app/core/user/user.service";
import {cloneDeep} from "lodash-es";
import {environment} from "environments/environment";
import { HttpClient } from "@angular/common/http";
import {ToastrService} from "ngx-toastr";
import {TranslocoService} from "@ngneat/transloco";
import {IUser} from 'app/interfaces/IUser'
import {EventMessagesService, eventType} from "@store/event-messages.service";
import Bugsnag from '@bugsnag/js'
import {AppModeService} from "@store/services/app-mode.service";
import {AppMode} from "../../../enums/AppModeEnum";
import {SurveyViewerService} from "../../survey/survey-viewer/services/survey-viewer.service";

export interface extStorage {
  extDef: {},
  ask: string[],
  neverAsk: string[],
  randomAssign: string[]
}

export interface askExtStatus {
  [key: string]: {
    exist: boolean;
    pass: boolean;
  };
}

@Injectable({
  providedIn: 'root'
})
export class ExtProfilesService {

  BASE_URL = environment.apiUrl;

  private _localStorage: extStorage = {
    extDef: {},
    ask: [],
    neverAsk: [],
    randomAssign: []
  }

  public extDef: any;

  private lastCollectionStatus = false;
  collectStatus$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

  extProfilesToAsk$: BehaviorSubject<askExtStatus> = new BehaviorSubject<askExtStatus>({});
  randomAssign$: BehaviorSubject<string[]> = new BehaviorSubject<string[]>([]);
  unAnsweredIndex$: BehaviorSubject<string[]> = new BehaviorSubject<string[]>([]);
  extProfilesToAskIndex$: BehaviorSubject<string[]> = new BehaviorSubject<string[]>([]);
  nextToAsk$: BehaviorSubject<string> = new BehaviorSubject<string>(null);
  hasPendingRequest$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false)

  constructor(
    private userService: UserService,
    private _httpClient: HttpClient,
    private toastrService: ToastrService,
    private translate: TranslocoService,
    private eventMessagesService: EventMessagesService,
    private appModeService: AppModeService,
  ) {

    this.userService.user$.subscribe((aUser) => {
      this.calculateToAsk();
    });

  }

  // -----------------------------------------------------------------------------------------------------
  // @ Accessors
  // -----------------------------------------------------------------------------------------------------
  set(values: extStorage = null, alreadyAnswered = []) {

    try {

      if (values.ask?.length === 0) {
        this.collectStatus$.next(true);
      }

      this._localStorage = values;
      this.extDef = values.extDef;

      const extPToAsk: askExtStatus = {}

      values.ask.forEach((anE) => {

        extPToAsk[anE] = {
          pass: alreadyAnswered.includes(anE),
          exist: alreadyAnswered.includes(anE),
        };

        if (!values.extDef[anE]) {
          this.toastrService.warning(
            `${this.translate.translate('L\'extProfile')}<a href="${environment.clientAppUrl}/projects/extprofils" target="_blank"><pre>${anE}</pre></a>${this.translate.translate('est introuvable dans la bibliothèque.')}<br>${this.translate.translate('Vérifier qu\'il existe bien dans les extProfliles.')}`,
            anE,
            {
              timeOut: 10000,
              progressBar: true,
              enableHtml: true,
            }
          )
        }

      })

      this.extProfilesToAsk$.next(extPToAsk);
      this.calculateToAsk();

    } catch (e) {
      console.error('[ext-profiles.service] Error setting exts', e);
    }

  }

  setExtDef(extDef: any) {
    this.extDef = extDef;
  }

  setAll(exts: string[] = []) {

    try {
      const init = {};
      exts?.forEach((key) => {
        init[key] = '';
      });
      this.updateMultipleExtProfiles(init);
    } catch (e) {
      console.error('Error setting all  exts', e);
    }

  }

  getAsk(): string[] {
    return this._localStorage.ask;
  }

  getRandomAssign(): string[] {
    return this._localStorage.randomAssign;
  }

  getNeverAsk(): string[] {
    return this._localStorage.neverAsk;
  }

  extHasParams(keyName: string): boolean {
    return !!this.extDef?.[keyName];
  }

  getParams(keyName: string): any[] {
    const selectedExt = this.extDef?.[keyName];
    if (
      selectedExt &&
      selectedExt.data &&
      selectedExt.data.params
    ) {
      return selectedExt.data.params
    }
    return [];

  }

  // -----------------------------------------------------------------------------------------------------
  // @ Public methods
  // -----------------------------------------------------------------------------------------------------
  /**
   * Update and calculated needed values.
   *
   */
  getExtProfiles() {
    return this.userService.user.extProfiles;
  }

  updateExtProfiles(key: string, value: string) {

    let nUser: IUser = cloneDeep(this.userService.user);

    nUser = {
      ...nUser,
      extProfiles: {
        ...nUser?.extProfiles || {},
        [key]: value,
      }
    }

    //
    // Post traitement ?
    //
    this.userService.user$.next(nUser);
    this.postUpdateExt(key, value);

  }

  persistUserExtProfiles(key, value): Observable<boolean> {

    this.hasPendingRequest$.next(true)

    if (this.userService.user$.value.isPublic || !value || this.appModeService.isTestSurvey$.value) {
      this.hasPendingRequest$.next(false)
      return of(false)
    }

    const list = [
      {
        keyName: key,
        keyValue: value
      }
    ]

    const endpoint = `${this.BASE_URL}/user/setExtprofils`
    this._httpClient.post(endpoint, {list: list})
      .subscribe({
        next: () => {
          this.hasPendingRequest$.next(false)
        },
        error: err => {
          this.hasPendingRequest$.next(false)
          Bugsnag.notify(err, (event) => {
            event.addMetadata('Ext Profiles Service', {
              method: this.persistUserExtProfiles.name,
              body: list,
              endpoint
            })
          })
        }
      })
    return of(true)
  }

  updateMultipleExtProfiles(exts: Record<string, string>) {

    let nUser: IUser = cloneDeep(this.userService.user);

    nUser = {
      ...nUser,
      extProfiles: {
        ...nUser?.extProfiles || {},
        ...exts,
      }
    }

    //
    // Missing Logs....
    //
    // Object.keys(exts).forEach((aKey) => {
    //   if (exts[aKey]?.length > 0) {
    //     this.eventMessagesService.emitEvent(eventType.extUpdateValue, {key: aKey, value: exts[aKey]})
    //   }
    // })

    // ?
    this.userService.user$.next(nUser);
    Object.keys(exts).forEach((key) => {
      this.postUpdateExt(key, exts[key]);
    })

    // this.checkIfAllExtCollected();

  }

  clearExtProfiles() {

    const extKeys = Object.keys(this.userService.user.extProfiles);
    const clearedKeys = {};
    extKeys.forEach((aKey) => {
      clearedKeys[aKey] = '';
    })

    this.userService.user$.next({
        ...this.userService.user,
        extProfiles: clearedKeys,
      }
    )
  }

  nextAll() {

    const newExtProfilesToAskList: askExtStatus = cloneDeep(this.extProfilesToAsk$.value);
    Object.keys(newExtProfilesToAskList).forEach((anExt) => {
      newExtProfilesToAskList[anExt] = {
        ...newExtProfilesToAskList[anExt],
        exist: true,
        pass: true,
      };
    });
    this.extProfilesToAsk$.next(newExtProfilesToAskList);
    this.extProfilesToAskIndex$.next(Object.keys(newExtProfilesToAskList)); // <--- pas que les pas répondus...

  }

  removeExtProfile(key: string) {

    let extProfiles = cloneDeep(this.userService.user.extProfiles);
    delete extProfiles[key];
    this.userService.user$.next({
      ...this.userService.user,
      extProfiles,
    });

  }

  doGetGeoIpInfos() {
    const action = 'getGeoIpLocation'
    const endpoint = `${this.BASE_URL}/4dm1n/publicActions`
    const body = {
      action,
      params: {},
    }

    return this._httpClient.post(endpoint, body)
      .pipe(
        map((response: any) => {
          this.updateMultipleExtProfiles({
            'internal_country': response.data.datas.country.isoCode,
            'address_components_city': response.data.datas.city.name,
          });

          this.userService.setLocalization(response.data.datas);

          return response;

        }),
        catchError(err => {
          Bugsnag.notify(err, (event) => {
            event.addMetadata('Ext Profiles Service', {
              method: this.doGetGeoIpInfos.name,
              body,
              endpoint
            })
          })

          return of(false)
        })
      );
  }

  randomAssign(ext: string[]) {

    ext.forEach((key) => {

      if (this.extDef[key]) {
        const randomIndex = Math.floor(Math.random() * this.extDef[key].data.params.length);
        const value = this.extDef[key].data['params'][randomIndex]['key']
        this.updateExtProfiles(key, `${value}`);

      }

    })

  }


  /**
   * Assign values to determine what has been replied or not...
   * --> [] => IExt object
   */
  nextExt(ext: string) {
    const newExtProfilesToAskList = cloneDeep(this.extProfilesToAsk$.value);
    newExtProfilesToAskList[ext].pass = true;
    this.extProfilesToAsk$.next(newExtProfilesToAskList);
    this.checkIfAllExtCollected();
  }

  /**
   * Update state to know what to ask next and what has been asked
   */
  private calculateToAsk() {

    const aUser: IUser = this.userService.user;

    if (Object.keys(this.extProfilesToAsk$.value).length > 0) {

      const userExtFilled = Object.keys(aUser?.extProfiles || {});

      const newExtProfilesToAskList: askExtStatus = cloneDeep(this.extProfilesToAsk$.value);
      const next = [];

      const unAnsweredIndex = [];

      Object.keys(newExtProfilesToAskList).forEach((anExt) => {
        if (!userExtFilled.includes(anExt)) {
          newExtProfilesToAskList[anExt] = {
            ...newExtProfilesToAskList[anExt],
            exist: false,
            pass: false,
          };
          unAnsweredIndex.push(anExt);
        } else {
          newExtProfilesToAskList[anExt] = {
            ...newExtProfilesToAskList[anExt],
            exist: true,
          };

        }

        //
        //
        //
        if (!newExtProfilesToAskList[anExt].exist && !newExtProfilesToAskList[anExt].pass) {
          next.push(anExt);
        }

      })

      this.nextToAsk$.next(next[0]);
      this.extProfilesToAsk$.next(newExtProfilesToAskList);
      this.extProfilesToAskIndex$.next(Object.keys(newExtProfilesToAskList)); // <--- pas que les pas répondus...
      this.unAnsweredIndex$.next(unAnsweredIndex);

      this.checkIfAllExtCollected();

    }

  }

  public checkIfAllExtCollected() {

    let collectOk = true;
    const extCollected = cloneDeep(this.extProfilesToAsk$.value);

    Object.keys(extCollected).forEach((anExt) => {
      if (!extCollected[anExt].exist || !extCollected[anExt].pass) {
        collectOk = false;
      }
    });

    // Si on en a pas ... on met à True!!
    if (Object.keys(this.extProfilesToAsk$.value).length === 0) {
      collectOk = true;
    }

    // Raise que si ça change...
    if (this.lastCollectionStatus !== collectOk) {
      this.collectStatus$.next(collectOk);
      this.lastCollectionStatus = collectOk;
    }

    if (collectOk) {
      this.appModeService.appMode$.next(AppMode.HOME);
      this.eventMessagesService.emitEvent(eventType.extAllCollected, {values: this.userService.user.extProfiles});
    }

  }

  setupRandom(){
    this.randomAssign$.getValue().forEach((key) => {
      if (this.extDef[key]) {
        const randomIndex = Math.floor(Math.random() * this.extDef[key].data.params.length);
        const value = this.extDef[key].data['params'][randomIndex]['key']
        this.updateExtProfiles(key, `${value}`);
      }
    });
    this.randomAssign$.next([]);
  }

  private postUpdateExt(keyName: string, value: string) {

    if (value?.length > 0 && !this.appModeService.isTestSurvey$.value) {
      this.eventMessagesService.emitEvent(eventType.extUpdateValue, {keyName, value});
    }

    this.persistUserExtProfiles(keyName, value)

    switch (keyName) {
      case 'internal_birthdate':
        const age = `${new Date().getFullYear() - parseInt(value, 0)}`;
        if (!isNaN(Number(age))) {
          this.updateExtProfiles('internal_age', age);
          this.updateExtProfiles('calculated_age', age);
          this.decodeAgeRange(age);
        }

        break;

      // case 'internal_age':
      //     break;

      default:
        // On fait rien...
        return of(true);
    }


  }

  private decodeAgeRange(age) {

    // sql += '\n     IF(calculatedAge.age < 20, \'< 20\',';
    // sql += '\n        IF(calculatedAge.age BETWEEN 20 AND 29, \'20 - 29\',';
    // sql += '\n           IF(calculatedAge.age BETWEEN 30 AND 39, \'30 - 39\',';
    // sql += '\n              IF(calculatedAge.age BETWEEN 40 AND 49, \'40 - 49\',';
    // sql += '\n                 IF(calculatedAge.age BETWEEN 50 AND 59, \'50 - 59\',';
    // sql += '\n                    IF(calculatedAge.age BETWEEN 60 AND 69, \'60 - 69\', \'> 70\')))))) key_value';

    age = parseInt(`${age}`, 0);

    let value = '< 20';

    if (age < 20) {
      value = '< 20';
      return this.updateExtProfiles('age_range', value);
    }

    if (age >= 20 && age <= 29) {
      value = '20 - 29';
      return this.updateExtProfiles('age_range', value);
    }

    if (age >= 30 && age <= 39) {
      value = '30 - 39';
      return this.updateExtProfiles('age_range', value);
    }

    if (age >= 40 && age <= 49) {
      value = '40 - 49';
      return this.updateExtProfiles('age_range', value);
    }

    if (age >= 50 && age <= 59) {
      value = '50 - 59';
      return this.updateExtProfiles('age_range', value);
    }

    if (age >= 60 && age <= 69) {
      value = '60 - 69';
      return this.updateExtProfiles('age_range', value);
    }

    if (age >= 70) {
      // value = '> 69';
      value = '70';
      return this.updateExtProfiles('age_range', value);
    }

  }


}

